import { useEffect, useRef, useState, type FormEvent } from 'react';

import { Button, Input, TextArea, Flex } from '@revenuemarket/cds';
import { ResponseStatus } from '@revenuemarket/http';
import { Toast } from '@revenuemarket/ui';
import { InputRef } from 'antd';
import { CategoryTreeItem } from '~apis/categories';
import { FlattenTreeResult } from '~components/ui/HorizontalTreeTable/Table';
import { TreeNodeId } from '~components/ui/HorizontalTreeTable/utils';
import { useAddCompanySpecificCategories } from '~hooks/queries/categories/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { CashFlowType } from '~models/cashFlow';
import { mixpanel } from '~pages/_app';

import {
  CATEGORY_DESCRIPTION_MAX_LENGTH,
  CATEGORY_DESCRIPTION_MAX_LENGTH_INFO,
} from './CategoryDescriptionTextArea';
import { CATEGORY_NAME_MAX_LENGTH, CATEGORY_NAME_MAX_LENGTH_INFO } from './CategoryNameInput';
import InfoItem from './InfoItem';

interface NewInfoProps extends CategoryTreeItem {
  onCancel: () => void;
  depth: number;
  parentId: TreeNodeId | null;
  flattenedTree: FlattenTreeResult<CategoryTreeItem>[];
  onSuccess?: (treeKey: string) => void;
}

const NewInfo: React.FC<NewInfoProps> = ({
  onCancel,
  onSuccess,
  parentId,
  flattenedTree,
  depth,
  ...props
}) => {
  const inputRef = useRef<InputRef>(null);
  const submittingRef = useRef(false);
  const { companyId } = useDemoAwareCompanyId();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const { mutateAsync: addCompanySpecificCategories, isLoading } =
    useAddCompanySpecificCategories();
  const category = props.categoryLabelKey?.split('_');
  const categoryId = Number(category?.[0]);

  const onAdd = async () => {
    if (name.length === 0 || isLoading || submittingRef.current) return;
    submittingRef.current = true;
    try {
      const siblingNames = flattenedTree
        .find((item) => item.key === parentId)
        ?.children.map((i) => i.value.name);
      if (siblingNames?.includes(name)) {
        Toast.error('동일한 이름의 계정이 이미 존재합니다.');
        return;
      }
      const parentIdParsed = parentId ? Number(String(parentId).split('_')[1]) : undefined;
      const response = await addCompanySpecificCategories({
        companyId,
        categoryId,
        request: {
          name,
          description,
          parentId: depth === 2 ? undefined : parentIdParsed,
        },
      });
      if (response.Status === ResponseStatus.SUCCESS) {
        Toast.success('계정이 추가되었습니다.');
        mixpanel.track(MIXPANEL_EVENT.CATEGORY.CREATE_DETAILED_CATEGORY, {
          depth: depth - 1,
        });
        mixpanel.track(MIXPANEL_EVENT.ENGAGEMENT.DEFAULT, {
          type: 'CREATE_DETAILED_CATEGORY',
        });
        onSuccess?.(response.Data?.data?.categoryTreeKey ?? '');
      } else {
        Toast.error('계정 추가에 실패했습니다.');
      }
    } finally {
      submittingRef.current = false;
    }
  };

  // // 처음 들어왔을 때 input에 focus
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onAdd();
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <Flex
        gap={12}
        direction="column"
        style={{
          width: '100%',
        }}
      >
        <InfoItem
          title="구분"
          description={props.cashFlowType === CashFlowType.IN ? '입금' : '출금'}
        />
        <InfoItem
          title="계정 이름"
          titleStyle={{
            display: 'inline-block',
            paddingTop: 6,
          }}
          description={
            <Input
              ref={inputRef}
              style={{ width: '100%' }}
              value={name}
              onChange={(e) => setName(e.target.value)}
              inputStatus={name.length > CATEGORY_NAME_MAX_LENGTH ? 'warning' : undefined}
              additionalText={
                name.length > CATEGORY_NAME_MAX_LENGTH ? CATEGORY_NAME_MAX_LENGTH_INFO : undefined
              }
            />
          }
        />
        <InfoItem
          title="계정 설명"
          titleStyle={{
            display: 'inline-block',
            paddingTop: 6,
          }}
          description={
            <TextArea
              style={{
                width: '100%',
              }}
              value={description}
              inputStatus={
                description.length > CATEGORY_DESCRIPTION_MAX_LENGTH ? 'warning' : undefined
              }
              onChange={(e) => setDescription(e.target.value)}
              additionalText={
                description.length > CATEGORY_DESCRIPTION_MAX_LENGTH
                  ? CATEGORY_DESCRIPTION_MAX_LENGTH_INFO
                  : undefined
              }
            />
          }
        />

        <Flex justify="flex-end" gap={4}>
          <Button type="button" onClick={onCancel}>
            취소
          </Button>
          <Button buttonType="primary" type="submit" disabled={isLoading || !name}>
            만들기
          </Button>
        </Flex>
      </Flex>
    </form>
  );
};

export default NewInfo;
